import { AmplitudeClient } from "amplitude-js";
import config from "./config";
import { LiveTestState, Results, TestType } from "./queries/types";
import { AuthenticationProvider } from "./types/authentication";
import { userHasConsentedToMarketing } from "./utils";

const amplitude = !(typeof window === "undefined")
  ? require("amplitude-js")
  : null;

let instance: AmplitudeClient | undefined;

function startAmplitude() {
  if (
    amplitude &&
    !instance &&
    config.amplitudeApiKey &&
    userHasConsentedToMarketing()
  ) {
    instance = amplitude?.getInstance();

    instance?.init(config.amplitudeApiKey, undefined, {
      includeReferrer: true,
      includeUtm: true,
    });

    instance?.setUserProperties({
      "app version": "next",
    });
  }
}

// Try to start amplitude as soon as window loads
startAmplitude();

// add event listener to start amplitude when user consent is ready
// this is needed because the user consent might not be ready when window loads
if (typeof window !== "undefined") {
  window.addEventListener("CookiebotOnConsentReady", startAmplitude);
}

export const setUserId = (userId: string | null) => {
  instance?.setUserId(userId);
};

export const setUserLocale = (locale: string) => {
  instance?.setUserProperties({ locale });
};

interface Test {
  slug: string;
  status: LiveTestState;
  type: TestType;
  duration: number;
  started: string;
  finished?: string;
}

export const setTest = (test: Test) => {
  const identify = new amplitude.Identify();

  identify.set("test slug", test.slug);
  identify.set("test type", test.type);
  identify.set("test status", test.status);
  identify.set("test duration", test.duration);

  if (test.started) {
    identify.set("test started", test.started);
  } else {
    identify.unset("test started");
  }

  if (test.finished) {
    identify.set("test finished", test.finished);
  } else {
    identify.unset("test finished");
  }

  instance?.identify(identify);
};

const getCommonProperties = (testId: string) => ({
  "job opening id": testId,
});

export const trackTestView = (testId: string) => {
  instance?.logEvent("view test", getCommonProperties(testId));
};

export const trackThirdPartyLogin = (
  testId: string,
  provider: AuthenticationProvider,
) => {
  instance?.logEvent("third party login", {
    ...getCommonProperties(testId),
    provider: provider,
  });
};

export const trackEmailLogin = (testId: string) => {
  instance?.logEvent("email login", getCommonProperties(testId));
};

export const trackConfirmIdentity = (testId: string) => {
  instance?.logEvent("confirm identity", getCommonProperties(testId));
};

export const trackDeclineIdentity = (testId: string) => {
  instance?.logEvent("decline identity", getCommonProperties(testId));
};

export const trackTestStart = (testId: string, testType: TestType) => {
  instance?.logEvent("start test", {
    ...getCommonProperties(testId),
    "test type": testType,
  });
};

export const trackPracticeTestStart = (testId: string) => {
  instance?.logEvent("start practice test", getCommonProperties(testId));
};

export const trackTestSubmit = (
  testId: string,
  result: Results,
  testType?: TestType,
) => {
  instance?.logEvent("submit test", {
    ...getCommonProperties(testId),
    "test type": testType,
    "passed test": result?.result === "passed",
    "test score": result?.score,
  });
};

export const trackPracticeTestSubmit = (testId: string) => {
  instance?.logEvent("submit practice test", getCommonProperties(testId));
};

export const trackTestTimeout = (testId: string) => {
  instance?.logEvent("time out test", getCommonProperties(testId));
};

export const trackQuestionSelect = (testId: string, index: number) => {
  instance?.logEvent("select question", {
    ...getCommonProperties(testId),
    "question number": index + 1,
  });
};

export const trackQuestionAnswer = (testId: string, index: number) => {
  instance?.logEvent("answer question", {
    ...getCommonProperties(testId),
    "question number": index + 1,
  });
};

export const trackResponseFormSubmit = (
  testId: string,
  positiveFeedback: boolean | null | undefined,
) => {
  let feedback: string;

  if (positiveFeedback != null) {
    feedback = positiveFeedback ? "positive" : "negative";
  } else {
    feedback = "none";
  }

  instance?.logEvent("submit response form", {
    ...getCommonProperties(testId),
    feedback: feedback,
  });
};

export const trackIntroVideoPlay = (testId: string) => {
  instance?.logEvent("play introduction video", getCommonProperties(testId));
};

export const trackStartRecordingVideo = (
  testId: string,
  questionId: number,
  questionIndex: number,
) => {
  instance?.logEvent("start recording video", {
    ...getCommonProperties(testId),
    "question id": questionId,
    "question number": questionIndex + 1,
  });
};

export const trackStopRecordingVideo = (
  testId: string,
  questionId: number,
  questionIndex: number,
) => {
  instance?.logEvent("stop recording video", {
    ...getCommonProperties(testId),
    "question id": questionId,
    "question number": questionIndex + 1,
  });
};

export const trackDeleteRecordedVideo = (
  testId: string,
  questionId: number,
  questionIndex: number,
) => {
  instance?.logEvent("delete recorded video", {
    ...getCommonProperties(testId),
    "question id": questionId,
    "question number": questionIndex + 1,
  });
};

export const trackUploadVideoStart = (testId: string, questionId: string) => {
  instance?.logEvent("start uploading video", {
    ...getCommonProperties(testId),
    "question id": questionId,
  });
};

export const trackUploadVideoFinish = (testId: string, questionId: string) => {
  instance?.logEvent("finish uploading video", {
    ...getCommonProperties(testId),
    "question id": questionId,
  });
};

export const trackUploadVideoFailure = (
  testId: string,
  questionId: string,
  extra?: Record<string, unknown>,
) => {
  instance?.logEvent("fail uploading video", {
    ...getCommonProperties(testId),
    "question id": questionId,
    ...extra,
  });
};

export const trackShowTimeIsUp = () => {
  instance?.logEvent("show time is up");
};

export const trackSwitchLocale = (locale: string) => {
  instance?.logEvent("switch locale", { locale });
};

export const trackCandidatePortal = (
  workspaceId: number,
  jobOpeningId: string,
  testSlug: string,
  testScore: number,
) => {
  instance?.logEvent("candidate portal", {
    "workspace id": workspaceId,
    ...getCommonProperties(jobOpeningId),
    "test slug": testSlug,
    "test score": testScore,
  });
};

export const trackShareAssessmentResultInLinkedin = (testId: string) => {
  instance?.logEvent("share assessment result in linkedin", {
    ...getCommonProperties(testId),
  });
};
